import React, { createContext, useState, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { onAuthStateChanged } from 'firebase/auth';
import authService from '../services/auth.service';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Listen to Firebase auth state changes
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                // User is signed in with Firebase
                // Force admin role for the specific email on state restore
                if (firebaseUser.email === 'admin@yatharthaweb.com') {
                    const adminUserData = {
                        id: firebaseUser.uid,
                        email: firebaseUser.email,
                        name: firebaseUser.displayName || 'Admin',
                        role: 'admin'
                    };
                    setUser(adminUserData);
                    localStorage.setItem('yathartha_auth', JSON.stringify(adminUserData));
                } else {
                    const storedUser = authService.getCurrentUser();
                    if (storedUser && storedUser.email === firebaseUser.email) {
                        setUser(storedUser);
                    } else {
                        // Create user object from Firebase user
                        const userData = {
                            id: firebaseUser.uid,
                            email: firebaseUser.email,
                            name: firebaseUser.displayName || firebaseUser.email,
                            role: 'user'
                        };
                        setUser(userData);
                        localStorage.setItem('yathartha_auth', JSON.stringify(userData));
                    }
                }
            } else {
                // User is signed out from Firebase
                const storedUser = authService.getCurrentUser();
                if (storedUser && storedUser.role === 'franchise-admin') {
                    setUser(storedUser);
                } else {
                    setUser(null);
                    localStorage.removeItem('yathartha_auth');
                }
            }
            setLoading(false);
        });

        // Cleanup subscription
        return () => unsubscribe();
    }, []);

    const login = async (email, password) => {
        const result = await authService.login(email, password);
        if (result.success) {
            setUser(result.data);
        }
        return result;
    };

    const register = async (userData) => {
        const result = await authService.register(userData);
        if (result.success) {
            setUser(result.data);
        }
        return result;
    };

    const logout = () => {
        authService.logout();
        setUser(null);
    };

    useEffect(() => {
        let unsubscribeFranchise = null;

        if (user && user.role === 'franchise-admin' && user.id) {
            const adminRef = doc(db, 'franchiseAdmins', user.id);
            unsubscribeFranchise = onSnapshot(adminRef, (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.status !== 'active') {
                        // User has been deactivated
                        Swal.fire('Account Deactivated', 'Your franchise account is no longer active. You have been logged out.', 'warning');
                        logout();
                    }
                } else {
                    // Document deleted
                    Swal.fire('Account Deleted', 'Your franchise account no longer exists.', 'error');
                    logout();
                }
            });
        }

        return () => {
            if (unsubscribeFranchise) {
                unsubscribeFranchise();
            }
        };
    }, [user?.id, user?.role]);

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isFranchiseAdmin: user?.role === 'franchise-admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
